import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { track } from "../lib/analytics";

/**
 * "What you're actually giving them" — self-contained value panel.
 *
 * Ported from the updated quote.html prototype's `.value-panel` markup —
 * two-column at desktop, single-column below 900px, no CTAs, no icons.
 * Fires a one-time `value_panel_view` analytics event on intersection.
 *
 * Props:
 *   page — one of "home" | "employers" | "employers_result"
 *          (forwarded to analytics so we can distinguish surfaces)
 */
export default function ValuePanel({ page }) {
    const { t } = useTranslation();
    const ref = useRef(null);
    const firedRef = useRef(false);

    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") {
            // Fallback: assume visible and fire on mount.
            if (!firedRef.current) {
                firedRef.current = true;
                track("value_panel_view", { page });
            }
            return undefined;
        }
        const node = ref.current;
        if (!node) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !firedRef.current) {
                        firedRef.current = true;
                        track("value_panel_view", { page });
                        io.disconnect();
                        break;
                    }
                }
            },
            { threshold: 0.25, rootMargin: "0px 0px -10% 0px" },
        );
        io.observe(node);
        return () => io.disconnect();
    }, [page]);

    return (
        <section
            ref={ref}
            className="value-panel-section"
            aria-labelledby={`value-panel-title-${page}`}
            data-testid={`value-panel-${page}`}
        >
            <div className="value-panel">
                <div className="value-panel-col-left">
                    <div className="section-label mono">
                        {t("valuePanel.label")}
                    </div>
                    <h2
                        id={`value-panel-title-${page}`}
                        className="value-panel-title"
                    >
                        {t("valuePanel.titleLead")}{" "}
                        <em>{t("valuePanel.titleAccent")}</em>
                    </h2>
                </div>
                <div className="value-panel-col-right">
                    <p className="value-panel-thesis">
                        {t("valuePanel.p1")}
                    </p>
                    <p className="value-panel-body">
                        {t("valuePanel.p2")}
                    </p>
                    <p
                        className="value-panel-body"
                        dangerouslySetInnerHTML={{ __html: t("valuePanel.p3") }}
                    />
                    <p className="value-panel-closer">
                        {t("valuePanel.p4")}
                    </p>
                </div>
            </div>
        </section>
    );
}
